import { useMemo, useState, useRef } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ColumnHeader from "./ColumnHeader";
import EmptyState from "../Shared/EmptyState";
import AppointmentHistory from "../Appointment/AppointmentHistory";
import SectionWrapper from "./SectionWrapper";
import AppointmentHistoryRow from "../Appointment/AppointmentHistoryRow";
import { APPOINTMENT_STATUS } from "../../config/appointment";
import { DateTime } from "luxon";
import { patchAppointment } from "../../api/appointmentApi.js";
import Modal from "../Shared/Modal";
import CancelAppointmentForm from "../Appointment/CancelAppointmentForm";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { useToast } from "../../hooks/useToast";

const AppointmentSection = ({
  appointments,
  nextAppointment,
  onCreate,
  onView,
  onEdit,
  isClientArchived = false,
  pagination,
}) => {
  const [showCancelDrawer, setShowCancelDrawer] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const queryClient = useQueryClient();
  const { is12HourClock } = useUserPreferences();
  const toast = useToast();

  const formatAppointmentTime = (time) => {
    const localTime = DateTime.fromISO(time, {
      zone: "utc",
    }).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const format = is12HourClock
      ? "MMMM dd, yyyy, h:mm a"
      : "MMMM dd, yyyy, HH:mm";
    return localTime.toFormat(format);
  };

  const formatAppointment = (appointment) => {
    const status = APPOINTMENT_STATUS.find((x) => x.id === appointment.status);
    return {
      ...appointment,
      id: appointment.id,
      statusId: status?.id,
      rawStartTime: appointment.startTime,
      startTime: formatAppointmentTime(appointment.startTime),
      endTime: formatAppointmentTime(appointment.endTime),
      status: status?.name,
      statusColour: status?.colour,
      address: appointment.serviceAddress?.full,
      isNextAppointment: appointment.id === nextAppointment?.id,
    };
  };

  const {
    mutate: mutatePatchAppointment,
    mutateAsync: mutatePatchAppointmentAsync,
  } = useMutation({
    mutationFn: patchAppointment,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["clientAppointments"] });

      const previousQueries = queryClient.getQueriesData({ queryKey: ["clientAppointments"] });
      const newStatus = variables.data?.status;

      if (newStatus) {
        previousQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old?.appointments) return old;
            return {
              ...old,
              appointments: old.appointments.map((apt) =>
                apt.id === variables.id ? { ...apt, status: newStatus } : apt
              ),
            };
          });
        });
      }

      return { previousQueries };
    },
    onSuccess: (data, variables) => {
      // Show toast based on the status change or if it's a cancellation
      if (variables.data.status === "cancelled") {
        toast.warning("Appointment Cancelled", "Appointment has been cancelled successfully");
      } else {
        // For status updates from the menu items
        const statusObj = APPOINTMENT_STATUS.find(s => s.id === variables.data.status);
        if (statusObj) {
          switch (statusObj.id) {
            case "confirmed":
              toast.success("Appointment Confirmed", "Appointment status updated to confirmed");
              break;
            case "completed":
              toast.success("Appointment Completed", "Appointment marked as completed");
              break;
            case "pending":
              toast.info("Appointment Pending", "Appointment status changed to pending");
              break;
            default:
              toast.success("Status Updated", `Appointment status changed to ${statusObj.name}`);
          }
        }
      }

      closeDrawerHandler();
    },
    onError: (err, variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          if (data !== undefined) queryClient.setQueryData(queryKey, data);
        });
      }
      // Inline status updates surface via toast; cancel form handles its own error.
      const errorMessage = err.response?.data?.error?.message || "An error occurred while updating the appointment";
      toast.error("Operation Failed", errorMessage);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["clientAppointments"] });
    },
  });

  const onUpdateStatusHandler = (appointmentId, newStatus) => {
    const matched = APPOINTMENT_STATUS.find(
      (status) => status.id === newStatus
    );
    if (!matched) return;

    mutatePatchAppointment({ id: appointmentId, data: { status: matched.id } });
  };

  const closeDrawerHandler = () => {
    setShowCancelDrawer(false);
    setSelectedAppointment(null);
    setIsFormDirty(false);
  };

  const onCancelHandler = (appointment) => {
    setShowCancelDrawer(true);
    setSelectedAppointment(appointment);
  };

  const appointmentList = useMemo(() => {
    return appointments.map((appointment) => {
      return formatAppointment(appointment);
    });
  }, [appointments, nextAppointment, is12HourClock]);

  const addAppointmentButton = !isClientArchived
    ? [
        {
          caption: "Add Appointment",
          onClick: onCreate,
          color: "blue",
          icon: PlusIcon,
        },
      ]
    : [];

  return (
    <SectionWrapper>
      {showCancelDrawer && (
        <Modal
          open={showCancelDrawer}
          onClose={closeDrawerHandler}
          title="Cancel Appointment"
          size="sm"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <CancelAppointmentForm
            appointment={selectedAppointment}
            patchAppointment={mutatePatchAppointmentAsync}
            closeDrawerHandler={closeDrawerHandler}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
          />
        </Modal>
      )}
      <ColumnHeader
        title={
          <div>
            <span>Appointment History</span>
            {isClientArchived && (
              <div className="mt-1">
                <p className="text-sm text-ink-600">
                  Client is archived - only existing appointments can be managed
                </p>
              </div>
            )}
          </div>
        }
        buttons={addAppointmentButton}
        belowHeader={
          nextAppointment && (
            <AppointmentHistoryRow
              appointment={formatAppointment(nextAppointment)}
              onView={onView}
              onEdit={onEdit}
              onUpdateStatus={onUpdateStatusHandler}
              onCancel={onCancelHandler}
            />
          )
        }
      />
      {appointments.length > 0 ? (
        <AppointmentHistory
          appointments={appointmentList}
          onView={onView}
          onEdit={onEdit}
          onUpdateStatus={onUpdateStatusHandler}
          onCancel={onCancelHandler}
        />
      ) : (
        <div className="my-8">
          <EmptyState
            text={nextAppointment ? "No more appointments" : "No appointments"}
            subText={
              isClientArchived
                ? "Client is archived - no new appointments can be created."
                : nextAppointment
                ? "Create another appointment."
                : "Get started by creating a new appointment."
            }
          />
        </div>
      )}
      {pagination}
    </SectionWrapper>
  );
};

export default AppointmentSection;
